import React, { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';

import { DesktopOnly, MobileOnly } from 'shared/components';
import { ERNIE_TYPES } from 'shared/constants';
import meConsumer from 'shared/graphql/user/queries/me-consumer.gql';
import useErnie from 'shared/hooks/use-ernie';
import { ModalWrapper } from 'shared/modals/v2-wrapper';

import { BottomDrawer, ModalContainer } from 'src/components/core';
import { InlinePhoneVerification } from 'src/components/loyalty/modal/inline-phone-verification';
import { withModalControlsHook } from 'src/components/modals/with-modal-controls-hook';
import type { ModalComponentProps } from 'src/components/modals/with-modal-controls-hook';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetRewardsV2Document } from 'src/gql/graphql';
import type { MeConsumerQuery } from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import { useSwitchToOtherAccount } from 'src/hooks/use-switch-to-other-account';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('loyalty-redemption-phone-verification');

export type LoyaltyRedemptionPhoneVerificationScope = {
  dispensaryId?: string;
  enterpriseId?: string;
};

type VerificationResult = { verified: boolean };
type VerificationModalProps = ModalComponentProps<LoyaltyRedemptionPhoneVerificationScope, VerificationResult>;

type VerificationDependencies = {
  open: (scope: LoyaltyRedemptionPhoneVerificationScope) => Promise<VerificationResult>;
  refreshUser: () => Promise<MeConsumerQuery['meConsumer']>;
  setUser: (user: MeConsumerQuery['meConsumer']) => void;
  refreshRewards: () => Promise<void>;
  onRefreshError: (error: unknown) => void;
};

export async function completeLoyaltyRedemptionPhoneVerification(
  scope: LoyaltyRedemptionPhoneVerificationScope,
  { open, refreshUser, setUser, refreshRewards, onRefreshError }: VerificationDependencies
): Promise<void> {
  const result = await open(scope);
  if (!result.verified) {
    return;
  }

  try {
    const user = await refreshUser();
    setUser(user);
    await refreshRewards();
  } catch (error) {
    onRefreshError(error);
  }
}

export const LoyaltyRedemptionPhoneVerificationModal = ({
  onClose,
  containerProps,
  data,
}: VerificationModalProps): JSX.Element => {
  const User = useUser();
  const phone = useObserver(() => User.phone);
  const switchToOtherAccount = useSwitchToOtherAccount();

  const content = (
    <InlinePhoneVerification
      dispensaryId={data.dispensaryId}
      enterpriseId={data.enterpriseId}
      initialPhone={phone}
      purpose='redemption'
      onVerified={() => onClose({ verified: true })}
      onClose={() => onClose({ verified: false })}
      onLoginToOtherAccount={() => {
        onClose({ verified: false });
        void switchToOtherAccount();
      }}
    />
  );

  return (
    <>
      <DesktopOnly mediaQuery='sm'>
        <ModalWrapper open onClose={() => onClose({ verified: false })}>
          <ModalContainer width='470px' p='32px' {...containerProps}>
            {content}
          </ModalContainer>
        </ModalWrapper>
      </DesktopOnly>
      <MobileOnly mediaQuery='xs'>
        <BottomDrawer
          open
          hideExitIcon
          onClose={() => onClose({ verified: false })}
          shouldCalculateIframeBottom
          {...containerProps}
        >
          {content}
        </BottomDrawer>
      </MobileOnly>
    </>
  );
};

const [
  LoyaltyRedemptionPhoneVerificationController,
  useLoyaltyRedemptionPhoneVerificationModal,
] = withModalControlsHook<LoyaltyRedemptionPhoneVerificationScope, VerificationResult>(
  LoyaltyRedemptionPhoneVerificationModal
);

export const useLoyaltyRedemptionPhoneVerification = (activeEnterpriseId?: string): (() => Promise<void>) => {
  const { dispensary } = useDispensary();
  const User = useUser();
  const showErnie = useErnie();
  const apolloClient = useApolloClient();
  const { open } = useLoyaltyRedemptionPhoneVerificationModal();

  return useCallback(async (): Promise<void> => {
    const scope = activeEnterpriseId
      ? { enterpriseId: activeEnterpriseId }
      : { dispensaryId: dispensary?.id, enterpriseId: dispensary?.retailer?.enterpriseId };

    await completeLoyaltyRedemptionPhoneVerification(scope, {
      open,
      refreshUser: async () => {
        const { data } = await apolloClient.query<MeConsumerQuery>({ query: meConsumer, fetchPolicy: 'no-cache' });
        return data.meConsumer;
      },
      setUser: (user) => User.setUser(user),
      refreshRewards: async () => {
        await apolloClient.refetchQueries({ include: [GetRewardsV2Document] });
      },
      onRefreshError: (error) => {
        logger.error('Failed to refresh loyalty state after phone verification', { error });
        showErnie(
          'Your phone was verified, but rewards could not be refreshed. Please refresh the page and try again.',
          ERNIE_TYPES.DANGER
        );
      },
    });
  }, [User, activeEnterpriseId, apolloClient, dispensary?.id, dispensary?.retailer?.enterpriseId, open, showErnie]);
};

export { LoyaltyRedemptionPhoneVerificationController, useLoyaltyRedemptionPhoneVerificationModal };
